import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash.isempty';
import startCase from 'lodash.startcase';
import { ReactComponent as HandCoinsIcon } from '../../assets/images/svg/hand-coins.svg';
import { ReactComponent as InfoIcon } from '../../assets/images/svg/info-ico.svg';
import Paragraph from '../Paragraph/Paragraph';
import themeColors from '../../assets/styles/themeColors';
import { mediaBelow, pxToRem, pxToRemMd } from '../../assets/styles/helper';
import Button from '../Button/Button';
import ConfettiGif from '../../assets/images/svg/confetti.svg';
import config from '../../config';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import { formatMoneyNumeral } from '../../util/helpers/numeralHelpers';
import TransactionDetails from '../Modals/Transaction/TransactionDetails';
import {
  emptyPortfolioTransactionSingle,
  portfolioTransactionsSingleFetch,
} from '../../store/actions/portfolio/portfolioActions';
import {
  selectPortfolioSingleTransaction,
  selectPortfolioSingleTransactionError,
} from '../../store/selectors/portfolioTransactionsSelector';
import { PORTFOLIO_TRANSACTIONS_SINGLE_LOADING } from '../../store/actions/portfolio/portfolioActionConstants';
import { selectIsLoadingByActionType } from '../../store/selectors/loadingSelectors';
import { getCurrentAccountUid } from '../../store/selectors/accountSelectors';
import SectionLoader from '../Loader/SectionLoader';
import { ReactComponent as Close } from '../../assets/images/svg/close.svg';
import IconButton from '../IconButton/IconButton';
import { variables } from '../../assets/styles/variables';
import addressTagType, {
  ADDRESS_TAG_TYPES,
} from '../../util/enum/api/addressTagType';
import { parseEnumType } from '../../util/helpers/enumMappers';
import { productLocationTypes } from '../../util/enum/api/productTypes';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: ${pxToRem(16)};
  background-image: url(${ConfettiGif});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-color: #fff;

  ${mediaBelow(variables.breakpoints.bpMd)} {
    height: 100%;
    justify-content: flex-start;
  }
`;

const Title = styled.h2`
  margin: ${pxToRem(16)} 0 ${pxToRem(16)} 0;
  font-size: ${pxToRem(24)};
  font-weight: 500;
`;

const Text = styled.p`
  font-size: ${pxToRem(16)};
  margin-bottom: ${pxToRem(4)};
`;

const Highlight = styled.span`
  font-weight: 700;
  font-size: ${pxToRem(24)};
`;

const Price = styled.div`
  font-size: ${pxToRem(20)};
  margin-bottom: ${pxToRem(10)};
`;

const FinalPrice = styled.p`
  font-size: ${pxToRem(16)};
  margin-bottom: ${pxToRem(20)};
  color: ${themeColors.colorTextSecondary};
`;

const SavingsBox = styled.div`
  background-color: ${themeColors.colorPrimaryLight};
  padding: ${pxToRem(10)};
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  border: 1px solid #cceedb;
  border-radius: ${pxToRem(4)};
  margin-bottom: ${pxToRem(20)};
`;

const SavingInfo = styled(Paragraph)`
  font-size: ${pxToRem(14)};
  color: ${themeColors.colorSuccess};
  svg {
    display: inline-block;
    vertical-align: middle;
    margin-right: ${pxToRem(4)};
  }
`;

const InfoBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${pxToRem(4)};
  padding: ${pxToRem(12)} ${pxToRem(16)};
  border-radius: ${pxToRem(8)};
  background-color: ${themeColors.colorBackgroundSecondary};
  color: ${themeColors.colorTextSecondary};
  font-size: 14px;
  font-weight: 500;
  margin-bottom: ${pxToRem(16)};
  border: 1px solid ${themeColors.colorCardBorder};
  width: 100%;

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 12px;
  }
`;

const VaultTag = styled.span`
  font-size: ${pxToRem(12)};
  color: ${themeColors.colorWhite};
  font-weight: 600;
  background-color: ${(props) =>
    props.segregated
      ? themeColors.colorButtonPrimary
      : themeColors.colorSuccess};
  border-radius: ${variables.borderRadius.borderRadiusSm};
  display: inline-flex;
  padding: ${pxToRem(4)} ${pxToRem(6)};
  justify-content: center;
  align-items: center;
`;

const ButtonContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: ${pxToRem(16)};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    margin-top: auto;
  }
`;

const Image = styled.img`
  width: ${pxToRem(250)};
  height: ${pxToRem(250)};
  object-fit: contain;
`;

export const ModalClose = styled(IconButton)`
  &,
  svg {
    width: ${pxToRem(16)};
    height: ${pxToRem(16)};
    margin-left: auto;
  }

  ${mediaBelow(variables.breakpoints.bpMd)} {
    &,
    svg {
      width: ${pxToRemMd(24)};
      height: ${pxToRemMd(24)};
    }
  }
`;

const OrderConfirmation = ({
  wizardData,
  onButtonClick,
  setGoBackHandler,
  stepIndex,
  currentStepIndex,
  setWizardTitle,
  isPostPaidVisible,
  closeModal,
  isInstaVaultProduct,
  isSegregated,
  isProductPage,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    storageProduct,
    calculation,
    selectedPaymentMethod,
    transactionUid,
    selectedAddress,
    isVaultActiveTab,
    selectedVault,
    selectedPlan,
  } = wizardData;
  const settings = useSelector(selectSettings);
  const { RootUrl } = settings;

  const isSegregatedProduct = !isProductPage
    ? selectedPlan === 'premium'
    : isSegregated;
  const segregatedCaption = settings?.SegregatedCaption;
  const accountUid = useSelector(getCurrentAccountUid);

  useEffect(() => {
    if (stepIndex === currentStepIndex) {
      setGoBackHandler(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex, currentStepIndex]);

  useEffect(() => {
    setWizardTitle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const imageUrl = config.algoliaSearch.imageCode
    .replace('{product_code}', storageProduct.ProductCode)
    .replace('{base_url}', RootUrl);

  const [isTransactionDetailsOpen, setIsTransactionDetailsOpen] = useState(
    false,
  );
  const singleTransaction = useSelector(selectPortfolioSingleTransaction);
  const singleTransactionError = useSelector(
    selectPortfolioSingleTransactionError,
  );
  const singleTransactionLoading = useSelector(
    selectIsLoadingByActionType(PORTFOLIO_TRANSACTIONS_SINGLE_LOADING),
  );

  const onTransactionCardClick = useCallback(
    (transactionUid) => {
      dispatch(
        portfolioTransactionsSingleFetch({
          accountUid,
          transactionUid,
        }),
      );
      setIsTransactionDetailsOpen(true);
    },
    [accountUid, dispatch],
  );

  return (
    <>
      <SectionLoader isLoading={singleTransactionLoading}>
        <Container>
          <ModalClose onClick={closeModal} data-cy="button-close-modal">
            <Close />
          </ModalClose>
          <Image src={encodeURI(imageUrl)} alt="Product Name Value Etc..." />

          <Title>{t('buyWizard.congratulations')}</Title>
          <Text>{t('buyWizard.orderCompleteTitle')}</Text>
          <Price>
            <Highlight>
              {calculation?.quantity} × {storageProduct?.ShortCaption}
            </Highlight>
            <br />
            <FinalPrice>
              {' '}
              at {formatMoneyNumeral(calculation?.estimatedOrderTotal)}
            </FinalPrice>
          </Price>
          {isVaultActiveTab &&
            selectedVault?.LocationType &&
            !isInstaVaultProduct && (
              <InfoBanner>
                <InfoIcon fill={themeColors.colorTextSecondary} />
                {t('buyWizard.storingInVault', {
                  vaultName: startCase(
                    parseEnumType(
                      productLocationTypes,
                      selectedVault.LocationType,
                    ),
                  ),
                })}
                {isSegregatedProduct && segregatedCaption && (
                  <VaultTag segregated>{segregatedCaption}</VaultTag>
                )}
              </InfoBanner>
            )}
          {!isVaultActiveTab &&
            selectedAddress &&
            (() => {
              const cityState = [
                selectedAddress.City,
                selectedAddress.StateRegion,
              ]
                .filter(Boolean)
                .join(', ');
              const label =
                selectedAddress.TagType === ADDRESS_TAG_TYPES.OTHER
                  ? selectedAddress.TagNote
                  : addressTagType[selectedAddress.TagType];
              return (
                <InfoBanner>
                  <InfoIcon fill={themeColors.colorTextSecondary} />
                  {label
                    ? t('buyWizard.deliveringToWithLabel', { label, cityState })
                    : t('buyWizard.deliveringTo', { cityState })}
                </InfoBanner>
              );
            })()}
          {isPostPaidVisible && selectedPaymentMethod?.PaymentMethodType !== 3 && (
            <SavingsBox>
              <SavingInfo>
                <HandCoinsIcon />
                {t('buyWizard.savingInfo3')}
                <b>
                  {formatMoneyNumeral(calculation?.paymentTypeDiscount)} (4%)
                </b>
              </SavingInfo>
            </SavingsBox>
          )}
          <ButtonContainer>
            <Button
              variant="primaryOutlined"
              textTransform="none"
              style={{ width: '100%' }}
              onClick={() => {
                onTransactionCardClick(transactionUid);
              }}
            >
              {t('buyWizard.viewOrder')}
            </Button>
          </ButtonContainer>
        </Container>
      </SectionLoader>

      <TransactionDetails
        isOpen={!isEmpty(singleTransaction) && isTransactionDetailsOpen}
        onClose={() => {
          dispatch(emptyPortfolioTransactionSingle());
          setIsTransactionDetailsOpen(false);
          onButtonClick();
        }}
        transaction={singleTransaction}
        error={singleTransactionError}
      />
    </>
  );
};

OrderConfirmation.propTypes = {
  wizardData: PropTypes.shape({
    storageProduct: PropTypes.shape({
      ProductCaption: PropTypes.string,
      ProductPrice: PropTypes.number,
      Locations: PropTypes.arrayOf(PropTypes.shape({})),
      ProductCode: PropTypes.string,
      ShortCaption: PropTypes.string,
    }),
    calculation: PropTypes.shape({
      paymentTypeDiscount: PropTypes.number,
      estimatedOrderTotal: PropTypes.number,
      quantity: PropTypes.number,
    }),
    selectedPaymentMethod: PropTypes.shape({
      PaymentMethodType: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
    }),
    transactionUid: PropTypes.string,
    selectedAddress: PropTypes.shape({
      City: PropTypes.string,
      StateRegion: PropTypes.string,
      TagType: PropTypes.string,
      TagNote: PropTypes.string,
    }),
    isVaultActiveTab: PropTypes.bool,
    selectedVault: PropTypes.shape({
      LocationType: PropTypes.number,
    }),
    selectedPlan: PropTypes.string,
  }),
  onButtonClick: PropTypes.func,
  setWizardTitle: PropTypes.func,
  setGoBackHandler: PropTypes.func,
  stepIndex: PropTypes.number,
  currentStepIndex: PropTypes.number,
  isPostPaidVisible: PropTypes.bool,
  closeModal: PropTypes.func,
  isInstaVaultProduct: PropTypes.bool,
  isSegregated: PropTypes.bool,
  isProductPage: PropTypes.bool,
};

export default OrderConfirmation;
